import React, { useEffect, useRef, useState, useCallback } from 'react';
import './Organizacja.css';
import Login from './OrganizacjaLogin';
import { LocaleContext, useLocale, LANGUAGE_KEY, initialLanguage, translate, content, editContent } from './OrganizacjaI18n';
const id = () => window.crypto.randomUUID();
const shortDate = (value, language) => value ? new Intl.DateTimeFormat({
  pl: 'pl-PL',
  en: 'en-GB',
  es: 'es-ES'
}[language], {
  day: 'numeric',
  month: 'short',
  timeZone: 'UTC'
}).format(new Date(value + 'T12:00:00Z')) : '';
async function request(action, method = 'GET', body) {
  const response = await fetch('/api/organisation/' + action, {
    method,
    credentials: 'same-origin',
    cache: 'no-store',
    headers: {
      'Content-Type': 'application/json',
      'X-Taldigi-Request': '1'
    },
    body: body === undefined ? undefined : JSON.stringify(body)
  });
  let result;
  try {
    result = await response.json();
  } catch {
    throw new Error('Panel jest chwilowo niedostępny. Spróbuj ponownie.');
  }
  if (!response.ok) {
    const error = new Error(result.error || 'Nie udało się zapisać zmiany.');
    error.status = response.status;
    error.retryAfter = Number.isFinite(result.retryAfter) && result.retryAfter > 0 && result.retryAfter <= 86400 ? result.retryAfter : 0;
    throw error;
  }
  return result;
}
function Symbol({
  name = 'check',
  ...props
}) {
  const paths = {
    check: <path d="m5 12 4 4L19 6" />,
    lock: <><rect x="5" y="10" width="14" height="11" rx="3" /><path d="M8 10V7a4 4 0 0 1 8 0v3m-4 5v2" /></>,
    arrow: <path d="M5 12h14m-6-6 6 6-6 6" />,
    wrench: <path d="m14 7 3 3 4-4a6 6 0 0 1-8 8l-7 7-3-3 7-7a6 6 0 0 1 8-8Z" />,
    chess: <><path d="M7 20h10M8 17h8l-2-7H10l-2 7Zm1-7h6l2-5-5 2-5-2 2 5Z" /><path d="M12 2v5" /></>,
    people: <><circle cx="9" cy="7" r="3" /><path d="M3 20v-2a6 6 0 0 1 12 0v2m2-16a3 3 0 0 1 0 6m2 10v-2a6 6 0 0 0-2-4" /></>,
    flag: <><path d="M5 21V3m0 1c5-4 9 4 14 0v10c-5 4-9-4-14 0" /></>,
    star: <path d="m12 3 2.5 6.5L21 12l-6.5 2.5L12 21l-2.5-6.5L3 12l6.5-2.5L12 3Z" />,
    edit: <><path d="m15 4 5 5M4 20l5-1L21 7l-5-5L4 14v6Z" /></>,
    note: <><path d="M15 3H5v18h14V7l-4-4Zm0 0v5h4M8 12h8m-8 4h5" /></>
  };
  return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>{paths[name] || paths.note}</svg>;
}
function Task({
  task,
  disabled,
  onChange,
  onRemove
}) {
  const {
    t,
    language
  } = useLocale();
  const taskText = content(task, 'text', language);
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(taskText);
  const [due, setDue] = useState(task.due || '');
  const [remove, setRemove] = useState(false);
  useEffect(() => {
    setText(taskText);
    setDue(task.due || '');
  }, [taskText, task.due, language]);
  if (editing) return <li className="org-task-edit" data-unsaved={text !== taskText || due !== (task.due || '')}><form onSubmit={async e => {
      e.preventDefault();
      if (await onChange({
        ...editContent(task, {
          text: text.trim()
        }, language),
        due
      })) setEditing(false);
    }}><label>{t("Treść zadania")}<input value={text} onChange={e => setText(e.target.value)} maxLength={500} required /></label><label>{t("Termin (opcjonalnie)")}<input type="date" value={due} onChange={e => setDue(e.target.value)} /></label><div className="org-form-actions"><button className="org-primary" disabled={disabled || !text.trim()}>{t("Zapisz")}</button><button type="button" className="org-link" onClick={() => {
          setEditing(false);
          setText(taskText);
          setDue(task.due || '');
        }}>{t("Anuluj")}</button></div></form></li>;
  return <li className={'org-task' + (task.done ? ' is-done' : '')}><label className="org-task-check"><input type="checkbox" checked={task.done} disabled={disabled} onChange={() => onChange({
        ...task,
        done: !task.done
      })} /><span className="org-checkbox"><Symbol /></span><span>{taskText}{task.due && <small>{shortDate(task.due, language)}</small>}</span></label><button type="button" className="org-icon-button" aria-label={t("Edytuj: ") + taskText} onClick={() => setEditing(true)} disabled={disabled}><Symbol name="edit" /></button>{remove ? <button type="button" className="org-remove" onClick={onRemove} disabled={disabled}>{t("Potwierdź usunięcie")}</button> : <button type="button" className="org-icon-button" aria-label={t("Usuń: ") + taskText} onClick={() => setRemove(true)} disabled={disabled}>×</button>}{remove && <button type="button" className="org-link" onClick={() => setRemove(false)}>{t("Anuluj")}</button>}</li>;
}
function Group({
  group,
  index,
  disabled,
  onSave
}) {
  const {
    t,
    language
  } = useLocale();
  const groupTitle = content(group, 'title', language),
    groupSummary = content(group, 'summary', language),
    groupNotes = content(group, 'notes', language);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [notes, setNotes] = useState(groupNotes);
  const [edit, setEdit] = useState(false);
  const [title, setTitle] = useState(groupTitle);
  const [summary, setSummary] = useState(groupSummary);
  useEffect(() => {
    setNotes(groupNotes);
  }, [groupNotes, language]);
  useEffect(() => {
    setTitle(groupTitle);
    setSummary(groupSummary);
  }, [groupTitle, groupSummary, language]);
  useEffect(() => {
    setDraft('');
  }, [language]);
  const done = group.tasks.filter(t => t.done).length;
  const icon = {
    warsztat: 'wrench',
    juniorzy: 'people',
    hca: 'flag',
    trening: 'chess'
  }[group.id] || 'note';
  return <article data-unsaved={Boolean(draft) || notes !== groupNotes || (edit && (title !== groupTitle || summary !== groupSummary))} className={'org-group' + (group.priority === 'high' ? ' org-priority' : '')}><h2><button type="button" className="org-group-toggle" aria-expanded={open} aria-controls={'group-' + group.id} onClick={() => setOpen(!open)}><span className={'org-group-icon org-colour-' + index % 4}><Symbol name={icon} /></span><span className="org-group-heading"><span className="org-title-row">{groupTitle}{group.priority === 'high' && <small>{t("PRIORYTET")}</small>}</span><span className="org-group-summary">{groupSummary}</span></span><span className="org-group-count">{done}/{group.tasks.length}</span><span className="org-expand" aria-hidden="true">{open ? '−' : '+'}</span></button></h2>
    <div className="org-group-body" id={'group-' + group.id} hidden={!open}><div className="org-group-tools"><span>{group.tasks.length - done} {t("do zrobienia")}</span><button type="button" className="org-link" onClick={() => setEdit(!edit)}>{t("Edytuj temat")}</button></div>
      {edit && <form className="org-topic-form" onSubmit={async e => {
        e.preventDefault();
        if (await onSave(editContent(group, {
          title: title.trim(),
          summary: summary.trim()
        }, language))) setEdit(false);
      }}><label>{t("Nazwa")}<input value={title} maxLength={100} onChange={e => setTitle(e.target.value)} required /></label><label>{t("Krótki opis")}<input value={summary} maxLength={240} onChange={e => setSummary(e.target.value)} /></label><button className="org-primary" disabled={disabled || !title.trim()}>{t("Zapisz temat")}</button></form>}
      <ul className="org-tasks">{group.tasks.map(task => <Task key={task.id} task={task} disabled={disabled} onChange={next => onSave({
          ...group,
          tasks: group.tasks.map(t => t.id === next.id ? next : t)
        })} onRemove={() => onSave({
          ...group,
          tasks: group.tasks.filter(t => t.id !== task.id)
        })} />)}</ul>
      <form className="org-add-task" onSubmit={async e => {
        e.preventDefault();
        if (await onSave({
          ...group,
          tasks: [...group.tasks, {
            id: id(),
            language,
            text: draft.trim(),
            done: false,
            due: ''
          }]
        })) setDraft('');
      }}><label className="org-sr" htmlFor={'new-' + group.id}>{t("Nowe zadanie: ")}{groupTitle}</label><input id={'new-' + group.id} value={draft} maxLength={500} onChange={e => setDraft(e.target.value)} placeholder={t("Dopisz mały, konkretny krok…")} required /><button className="org-add-button" aria-label={t("Dodaj zadanie do ") + groupTitle} disabled={disabled || !draft.trim()}>+</button></form>
      <details className="org-group-notes"><summary>{t("Notatka do tematu")} {notes !== groupNotes && t("• niezapisana")}</summary><label className="org-sr" htmlFor={'notes-' + group.id}>{t("Notatka: ")}{groupTitle}</label><textarea id={'notes-' + group.id} value={notes} onChange={e => setNotes(e.target.value)} maxLength={4000} rows={4} /><button type="button" className="org-secondary" disabled={disabled || notes === groupNotes} onClick={() => onSave(editContent(group, {
          notes
        }, language))}>{t("Zapisz notatkę")}</button></details>
    </div>
  </article>;
}
function Inbox({
  value,
  onSave,
  disabled
}) {
  const {
    t, language
  } = useLocale();
  const [draft, setDraft] = useState(value);
  const lastValue=useRef({value,language});
  useEffect(() => {
    if(lastValue.current.value!==value || lastValue.current.language!==language){lastValue.current={value,language};setDraft(value);}
  }, [value, language]);
  return <section className="org-inbox" data-unsaved={draft !== value}><div className="org-panel-title"><Symbol name="note" /><h2>{t("Żeby nie zapomnieć")}</h2></div><p>{t("Wrzuć tutaj myśl. Uporządkujesz ją później.")}</p><label className="org-sr" htmlFor="org-inbox">{t("Szybka notatka")}</label><textarea id="org-inbox" rows={4} maxLength={6000} value={draft} onChange={e => setDraft(e.target.value)} placeholder={t("Co jeszcze mam na głowie?")} /><div className="org-notes-bottom"><small>{draft === value ? t("Notatka zapisana") : t("Masz niezapisane zmiany")}</small><button type="button" className="org-secondary" disabled={disabled || draft === value} onClick={() => onSave(draft)}>{t("Zapisz")}</button></div></section>;
}
function Assistant({
  messages,
  groups,
  disabled,
  onSend,
  onAdd
}) {
  const {
    t,
    language
  } = useLocale();
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [added, setAdded] = useState({});
  const lastLanguage=useRef(language);
  useEffect(()=>{if(lastLanguage.current!==language){lastLanguage.current=language;setDraft('');}},[language]);
  const end = useRef(null);
  const initial = useRef(true);
  useEffect(() => {
    if (initial.current) {
      initial.current = false;
      return;
    }
    end.current?.scrollIntoView({
      block: 'nearest'
    });
  }, [messages.length]);
  async function send(e) {
    e.preventDefault();
    if (!draft.trim()) return;
    setSending(true);
    setError('');
    try {
      await onSend(draft.trim());
      setDraft('');
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  }
  return <section className="org-assistant" data-unsaved={Boolean(draft)} id="org-assistant"><div className="org-panel-title"><Symbol name="star" /><h2>{t("Asystent planu")}</h2><span className="org-ai-label">AI</span></div><p>{t("Pomoc w planie, priorytetach i krótkich szkicach.")}</p><details className="org-ai-info"><summary>{t("Co pamięta asystent?")}</summary><p>{t("Twój zapisany plan i ostatnie wiadomości z tej rozmowy na stronie. Przegląd skrzynek i wysyłkę maili zleć w ChatGPT.")}</p></details>
    <div className="org-messages" aria-live="polite" aria-label={t("Rozmowa z asystentem")}>{messages.length === 0 ? <div className="org-chat-empty"><Symbol name="star" /><strong>{t("Od czego zaczynamy?")}</strong><p>{t("Opisz, co masz na głowie.")}<br />{t("Zamienimy to w następny krok.")}</p></div> : messages.map(message => <div key={message.id} className={'org-message org-message-' + message.role}><small>{message.role === 'user' ? t("TY") : t("ASYSTENT")}</small><p>{message.text}</p>{(message.suggestions || []).map((suggestion, index) => {
          const key = message.id + '-' + index;
          const group = groups.find(g => g.id === suggestion.groupId);
          const exists = group?.tasks.some(t => t.text === suggestion.text);
          return <div key={key} className="org-suggestion"><small>{group ? content(group, 'title', language) : t("Zadanie")}</small><p>{suggestion.text}</p><button type="button" className="org-secondary" disabled={disabled || added[key] || exists || !group} onClick={async () => {
              if (await onAdd(suggestion)) setAdded({
                ...added,
                [key]: true
              });
            }}>{added[key] || exists ? t("Dodane ✓") : t("+ Dodaj do planu")}</button></div>;
        })}</div>)}{sending && <p className="org-thinking">{t("Układam odpowiedź…")}</p>}<div ref={end} /></div>
    {messages.length === 0 && <div className="org-prompts">{[t("Co najpierw?"), t("Ułóż mi prosty plan dnia")].map(text => <button type="button" key={text} onClick={() => setDraft(text)}>{text}</button>)}</div>}
    <form onSubmit={send} className="org-chat-form"><label className="org-sr" htmlFor="org-chat">{t("Wiadomość do asystenta")}</label><textarea id="org-chat" rows={2} maxLength={2500} value={draft} onChange={e => setDraft(e.target.value)} placeholder={t("Napisz, co chcesz ogarnąć…")} required /><button className="org-primary" disabled={sending || disabled || !draft.trim()} aria-label={t("Wyślij do asystenta")}><Symbol name="arrow" /></button></form>{error && <p className="org-error" role="alert">{t(error)}</p>}<small className="org-ai-foot">{t("Sugestie dodajesz jednym kliknięciem. Limit: 30 pytań dziennie.")}</small>
  </section>;
}
export default function Organizacja() {
  const [language, setLanguage] = useState(initialLanguage);
  useEffect(() => {
    try {
      localStorage.setItem(LANGUAGE_KEY, language);
    } catch {}
  }, [language]);
  const t = useCallback(text => translate(text, language), [language]);
  return <LocaleContext.Provider value={{
    language,
    setLanguage,
    t
  }}><OrganizacjaContent /></LocaleContext.Provider>;
}
function ProgressRecord({
  group
}) {
  const {
    language
  } = useLocale();
  const [open, setOpen] = useState(false);
  return <article className="org-group org-record"><h2><button type="button" className="org-group-toggle" aria-expanded={open} aria-controls={'group-' + group.id} onClick={() => setOpen(!open)}><span className="org-group-icon org-colour-0"><Symbol /></span><span className="org-group-heading"><span className="org-title-row">{content(group, 'title', language)}</span><span className="org-group-summary">{content(group, 'summary', language)}</span></span><span className="org-expand" aria-hidden="true">{open ? '−' : '+'}</span></button></h2><div id={'group-' + group.id} hidden={!open} className="org-group-body"><p className="org-progress-note">{content(group, 'notes', language)}</p></div></article>;
}
function OrganizacjaContent() {
  const {
    language,
    setLanguage,
    t
  } = useLocale();
  const [state, setState] = useState('loading');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [saving, setSaving] = useState(false);
  const [newTopic, setNewTopic] = useState(false);
  const [topic, setTopic] = useState('');
  const [conflict, setConflict] = useState(false);
  const saveLock = useRef(false);
  const load = useCallback(async (afterVerification = false) => {
    setError('');
    try {
      const result = await request('session');
      setData(result);
      setState('ready');
      setConflict(false);
    } catch (e) {
      if (afterVerification === true) {
        if (e.status === 401) e.message = "Kod został potwierdzony, ale logowanie nie zostało zachowane. Spróbuj ponownie otworzyć plan. Jeśli błąd wróci, otwórz stronę w Safari.";
        throw e;
      }
      if (e.status === 401) {
        setData(null);
        setState('login');
      } else {
        setError(e.message);
        setState('error');
      }
    }
  }, []);
  useEffect(() => {
    const oldLang = document.documentElement.lang;
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex,nofollow';
    document.head.appendChild(meta);
    load();
    return () => {
      document.documentElement.lang = oldLang;
      meta.remove();
    };
  }, [load]);
  useEffect(() => {
    document.title = t('Organizacja') + ' | Taldigi';
    document.documentElement.lang = language;
  }, [language, t]);
  async function save(plan) {
    if (saveLock.current || conflict) return false;
    saveLock.current = true;
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const result = await request('plan', 'PUT', {
        plan,
        version: data.version
      });
      setData(current => ({
        ...current,
        ...result
      }));
      setNotice(t("Zmiany zapisane"));
      return true;
    } catch (e) {
      setError(e.message);
      if (e.status === 401) {
        setData(null);
        setState('login');
      }
      if (e.status === 409) setConflict(true);
      return false;
    } finally {
      saveLock.current = false;
      setSaving(false);
    }
  }
  async function logout() {
    setSaving(true);
    try {
      await request('logout', 'POST', {});
      setData(null);
      setState('login');
      setError('');
    } catch (e) {
      if (e.status === 401) {
        setData(null);
        setState('login');
      } else setError(e.message);
    } finally {
      setSaving(false);
    }
  }
  async function copyPlan() {
    const text = [t("Plan od ") + data.plan.weekStart, ...data.plan.groups.flatMap(g => ['', content(g, 'title', language), content(g, 'summary', language), ...g.tasks.map(task => (task.done ? '✓ ' : '□ ') + content(task, 'text', language) + (task.due ? ' (' + task.due + ')' : '')), content(g, 'notes', language)]), '', content(data.plan, 'inbox', language)].join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setNotice(t("Plan skopiowany. Możesz wkleić go do ChatGPT."));
    } catch {
      setError(t("Przeglądarka nie zezwoliła na kopiowanie. Zaznacz potrzebny tekst na stronie."));
    }
  }
  const plan = data?.plan;
  const all = plan?.groups.flatMap(g => g.tasks) || [];
  const done = all.filter(t => t.done).length;
  const endDate = plan ? new Date(plan.weekStart + 'T12:00:00Z') : new Date();
  endDate.setUTCDate(endDate.getUTCDate() + 6);
  return <div className="organisation"><div className="org-shell"><header className="org-header"><a href="/" className="org-brand">taldigi<span>.</span><span className="org-brand-divider">/</span><small>{t("organizacja")}</small></a><div className="org-header-actions"><label className="org-language"><span className="org-sr">{t("Język")}</span><select value={language} disabled={saving} onChange={e => {
              if (document.querySelector('.organisation [data-unsaved="true"]') && !window.confirm(t('Masz niezapisany tekst. Zmienić język i odrzucić ten tekst?'))) return;
              setTopic('');
              setLanguage(e.target.value);
            }}><option value="en">English</option><option value="pl">Polski</option><option value="es">Español</option></select></label><div className="org-private"><Symbol name="lock" /><span>{t("Tylko dla Ciebie")}</span>{state === 'ready' && <button type="button" className="org-link" onClick={logout} disabled={saving}>{t("Wyloguj")}</button>}</div></div></header>
    {state === 'loading' && <div className="org-loading" role="status">{t("Otwieram Twój plan…")}</div>}
    {state === 'error' && <div className="org-login"><h1>{t("Chwila przerwy.")}</h1><p className="org-error" role="alert">{t(error)}</p><button type="button" className="org-primary" onClick={load}>{t("Spróbuj ponownie")}</button></div>}
    {state === 'login' && <Login onLogin={() => load(true)} request={request} Icon={Symbol} />}
    {state === 'ready' && plan && <main><section className="org-intro"><div><p className="org-kicker">{t("TWÓJ TYDZIEŃ ·")} {shortDate(plan.weekStart, language)} – {shortDate(endDate.toISOString().slice(0, 10), language)} {endDate.getUTCFullYear()}</p><h1>{t("Po kolei")}<span>.</span></h1><p>{t("Jeden temat na raz. Reszta jest tutaj.")}</p></div><div className="org-progress"><span><strong>{done}</strong> / {all.length}</span><p>{t("małych kroków zrobionych")}</p><div className="org-progress-track" role="progressbar" aria-label={t("Ukończone zadania")} aria-valuemin={0} aria-valuemax={all.length || 1} aria-valuenow={done}><i style={{
                width: (all.length ? done / all.length * 100 : 0) + '%'
              }} /></div></div></section>
      <div className="org-mobile-links"><a href="#org-plan">{t("Mój plan ↓")}</a><a href="#org-assistant">{t("Zapytaj AI ↗")}</a></div>
      <div className="org-status-bar" role="status"><span className={saving ? 'org-saving' : ''}>{saving ? t("Zapisuję…") : t(notice) || t("Plan zapisany • dostępny na Twoich urządzeniach")}</span><button type="button" className="org-link" onClick={copyPlan}>{t("Kopiuj plan")}</button></div>
      {error && <div className="org-error org-global-error" role="alert">{t(error)}{conflict && <button type="button" className="org-secondary" onClick={load}>{t("Wczytaj aktualny plan")}</button>}</div>}
      <div className="org-layout"><div className="org-plan-column" id="org-plan"><div className="org-section-heading"><h2>{t("Najważniejsze sprawy")}</h2><span>{t("Rozwiń plusem")}</span></div><div className="org-groups">{[...plan.groups].sort((a, b) => Number(b.id === 'postep-2026-10-19') - Number(a.id === 'postep-2026-10-19')).map((group, index) => group.id === 'postep-2026-10-19' ? <ProgressRecord key={group.id} group={group} /> : <Group key={group.id} group={group} index={index} disabled={saving || conflict} onSave={next => save({
                ...plan,
                groups: plan.groups.map(g => g.id === next.id ? next : g)
              })} />)}</div>
        {newTopic ? <form data-unsaved={Boolean(topic)} className="org-new-topic" onSubmit={async e => {
              e.preventDefault();
              if (await save({
                ...plan,
                groups: [...plan.groups, {
                  id: id(),
                  language,
                  title: topic.trim(),
                  summary: '',
                  notes: '',
                  priority: 'normal',
                  tasks: []
                }]
              })) {
                setTopic('');
                setNewTopic(false);
              }
            }}><label htmlFor="org-topic">{t("Nazwa nowego tematu")}</label><input id="org-topic" value={topic} maxLength={100} onChange={e => setTopic(e.target.value)} required autoFocus placeholder={t("Np. nowy pomysł")} /><div className="org-form-actions"><button className="org-primary" disabled={saving || conflict || !topic.trim()}>{t("Dodaj temat")}</button><button type="button" className="org-link" onClick={() => setNewTopic(false)}>{t("Anuluj")}</button></div></form> : <button type="button" className="org-new-topic-button" onClick={() => setNewTopic(true)} disabled={saving || conflict || plan.groups.length >= 20}>{t("+ Nowy temat")}</button>}
        <Inbox value={content(plan, 'inbox', language)} disabled={saving || conflict} onSave={inbox => save(editContent(plan, {
              inbox
            }, language))} />
        <p className="org-language-hint">{t("Edytujesz tekst w wybranym języku. Nowe notatki pozostają w języku, w którym je zapiszesz.")}</p><div className="org-week-settings"><label htmlFor="org-week">{t("Początek planowanego tygodnia")}</label><input id="org-week" type="date" value={plan.weekStart} disabled={saving || conflict} onChange={e => {
                if (e.target.value) save({
                  ...plan,
                  weekStart: e.target.value
                });
              }} /></div>
      </div><aside><Assistant messages={data.messages || []} groups={plan.groups} disabled={saving || conflict} onSend={async message => {
              try {
                const result = await request('chat', 'POST', {
                  message,
                  language
                });
                setData(current => ({
                  ...current,
                  messages: result.messages
                }));
              } catch (e) {
                if (e.status === 401) {
                  setData(null);
                  setState('login');
                }
                throw e;
              }
            }} onAdd={suggestion => {
              const group = plan.groups.find(g => g.id === suggestion.groupId);
              if (!group) return false;
              return save({
                ...plan,
                groups: plan.groups.map(g => g.id === group.id ? {
                  ...g,
                  tasks: [...g.tasks, {
                    id: id(),
                    language,
                    text: suggestion.text,
                    done: false,
                    due: ''
                  }]
                } : g)
              });
            }} /></aside></div>
      <footer className="org-footer"><span>{t("Mały krok też się liczy.")}</span><span><Symbol name="lock" /> {data.email}</span></footer>
    </main>}
  </div></div>;
}
